import camelcaseKeys from 'camelcase-keys';

import { SafeStatuses, Statuses } from '../constants/statuses';
import { modifyArrayItemAt, removeArrayItemAt } from '../utils/arrayUtil';
import { isPast } from '../utils/dateUtil';
import { queryClient } from '../utils/queryClientUtil';

import insuredKeys from './models/insured/keys';

export const getPageData = (page, allPages) => {
  if (page.currentPage < page.lastPage || page.hasMore) {
    return page.currentPage + 1;
  }

  return undefined;
};

export const camelCaseApiResponse = portalApi => portalApi().then(res => camelcaseKeys(res, { deep: true }));

/**
 * reduce a react query infinite list to a single array
 * @param reactQueryData {object}
 * @return {array}
 */
export const getInfiniteListData = reactQueryData => {
  if (!reactQueryData) {
    return [];
  }

  return reactQueryData.pages.reduce((dataArray, page) => {
    dataArray = [...dataArray, ...page.data];
    return dataArray;
  }, []);
};

/**
 * get infinite list data from react query infinite list and map to value, label keys for dropdowns and multiselects
 * @param reactQueryData {object}
 * @param idKey {string},
 * @param labelKey {string}
 * @return {array}
 */
export const getAndMapInfiniteListData = (reactQueryData, idKey, labelKey) => {
  const data = getInfiniteListData(reactQueryData);

  return data?.map(item => ({
    value: item[idKey],
    label: typeof labelKey === 'function' ? labelKey(item) : item[labelKey]
  }));
};

/**
 * get page length of single react query infinite scroll page
 * @param reactQueryData {object}
 * @return {number}
 */
export const getInfiniteListDataLength = reactQueryData => {
  if (reactQueryData?.pages?.length) {
    return reactQueryData.pages[0].total;
  }

  return 0;
};

/**
 * get count of currently rendered data from react query infinite list
 * @param reactQueryData {object}
 * @return {number}
 */
export const getInfiniteListRenderedDataLength = reactQueryData => {
  if (!reactQueryData?.data?.pages?.length) {
    return 0;
  }

  return reactQueryData.data.pages.reduce((count, page) => {
    const dataLength = page?.data?.length;

    if (dataLength) {
      count += dataLength;
    }

    return count;
  }, 0);
};

/**
 * show if an infinite data query has data
 * @param reactQueryData {object}
 * @return {number}
 */
export const infiniteListDataDoesExist = reactQueryData => reactQueryData?.pages?.length && reactQueryData?.pages[0]?.data?.length;

/**
 * add a React Query infinite list array item
 * @param cacheKeys {array}
 * @param item {object}
 * @return {void}
 */
export const addInfiniteListsItem = (cacheKeys, item) => {
  cacheKeys.forEach(key => {
    const previousData = queryClient.getQueryData(key);

    if (!!previousData && previousData?.pages?.length) {
      queryClient.setQueryData(key, oldQueryData => {
        const updatedPages = oldQueryData.pages.reduce((dataArray, page) => {
          if (page.currentPage === page.lastPage) {
            const updatedData = [...page.data, item];

            return [
              ...dataArray,
              {
                ...page,
                data: updatedData
              }
            ];
          }

          return [...dataArray, page];
        }, []);

        return {
          ...oldQueryData,
          pages: updatedPages
        };
      });
    }
  });
};

/**
 * update a React Query infinite list array item with a matching value using a modification function
 * @param cacheKeys {array}
 * @param keyId {string}
 * @param itemId {string|number}
 * @param modifier {function}
 * @return {void}
 */
export const modifyInfiniteListsItem = (cacheKeys, keyId, itemId, modifier) => {
  cacheKeys.forEach(key => {
    const previousData = queryClient.getQueryData(key);

    if (!!previousData && previousData?.pages?.length) {
      queryClient.setQueryData(key, oldQueryData => {
        const updatedPages = oldQueryData.pages.reduce((dataArray, page) => {
          const updatedData = modifyArrayItemAt(
            page.data,
            keyId,
            itemId,
            modifier
          );

          return [
            ...dataArray,
            {
              ...page,
              data: updatedData
            }
          ];
        }, []);

        return {
          ...oldQueryData,
          pages: updatedPages
        };
      });
    }
  });
};

/**
 * delete a React Query infinite list array item with a matching value
 * @param cacheKeys {array}
 * @param keyId {string}
 * @param itemId {string|number}
 * @return {void}
 */
export const deleteInfiniteListsItem = (cacheKeys, keyId, itemId) => {
  cacheKeys.forEach(key => {
    const previousData = queryClient.getQueryData(key);

    if (!!previousData && previousData?.pages?.length) {
      queryClient.setQueryData(key, oldQueryData => {
        const updatedPages = oldQueryData.pages.reduce((dataArray, page) => {
          const updatedData = removeArrayItemAt(page.data, keyId, itemId);

          return [
            ...dataArray,
            {
              ...page,
              data: updatedData
            }
          ];
        }, []);

        return {
          ...oldQueryData,
          pages: updatedPages
        };
      });
    }
  });
};

export const setOverallStatusAndRecordStatusAsFetching = (insuredId, recordId) => {
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  // SET LOADING STATE FOR OVERALL STATUS
  queryClient.setQueryData(overallStatus, oldData => ({ ...oldData, isLoading: true }));

  // SET LOADING STATE FOR ACTIVE RECORD STATUSES
  modifyInfiniteListsItem(
    [activeRecordStatuses],
    'id',
    recordId,
    record => ({ ...record, isLoading: true })
  );
};

const generateObjectWithStatus = (currentObject, newStatus, activePoliciesOfType = []) => {
  if (newStatus === Statuses.Required) {
    let calcStatus = Statuses.Pending;

    if (!activePoliciesOfType.length) {
      calcStatus = Statuses.NotReceived;
    }

    const activeExpiredPolicyExists = activePoliciesOfType.find(p => isPast(p.expiresAt));

    if (activeExpiredPolicyExists) {
      calcStatus = Statuses.Expired;
    }

    return { ...currentObject, status: calcStatus };
  }

  if ([
    Statuses.Required,
    Statuses.Archived,
    Statuses.Active,
    Statuses.Deleted,
    Statuses.Updating,
    Statuses.Expired
  ].includes(newStatus)) {
    return {
      ...currentObject,
      isLoading: true
    };
  }

  return {
    ...currentObject,
    status: newStatus
  };
};

export const updatePolicyRequirementsWithLoadingStatus = ({
  policiesKey,
  policyRequirementsKey,
  itemRequirementsKey,
  previousPolicies,
  status,
  previousPolicyRequirements = [],
  previousItemRequirements = [],
  policyId = null,
  policyTypeId = null
}) => {
  if (!policyId && !policyTypeId) {
    throw new Error('You must provide a policyId or policyTypeId');
  }

  let activePoliciesOfType = [];

  if (policyTypeId) {
    activePoliciesOfType = previousPolicies.filter(p => p.insurancePolicyTypeId === policyTypeId);
  }

  if (policyId) {
    const policy = previousPolicies.find(p => p.id === policyId);
    activePoliciesOfType = previousPolicies.filter(p => p.insurancePolicyTypeId === policy.insurancePolicyTypeId);
  }

  // handle case for non required policy updates
  if (![...previousPolicyRequirements, ...previousItemRequirements].find(polReq => polReq.insurancePolicyTypeId === policyTypeId
    || polReq.insuredInsurancePolicyId === policyId
    || polReq.recordInsurancePolicyId === policyId)) {
    const newPolicyData = previousPolicies.map(policy => {
      if (policy.id === policyId) {
        return {
          ...policy,
          isLoading: true
        };
      }

      return policy;
    });

    queryClient.setQueryData(policiesKey, newPolicyData);

    return true;
  }

  // check policy requirements
  const newPolicyRequirementsData = previousPolicyRequirements.map(polReq => {
    if (policyTypeId) {
      if (polReq.insurancePolicyTypeId === policyTypeId) {
        return generateObjectWithStatus(polReq, status, activePoliciesOfType);
      }
    }

    if (policyId) {
      if (polReq.insuredInsurancePolicyId === policyId || polReq.recordInsurancePolicyId === policyId) {
        return generateObjectWithStatus(polReq, status, activePoliciesOfType);
      }
    }

    return polReq;
  });

  queryClient.setQueryData(policyRequirementsKey, newPolicyRequirementsData);

  const newItemRequirementsData = previousItemRequirements.map(itemReq => {
    if (policyTypeId) {
      if (itemReq.insurancePolicyTypeId === policyTypeId) {
        return generateObjectWithStatus(itemReq, status, activePoliciesOfType);
      }
    }

    if (policyId) {
      if (itemReq.insuredInsurancePolicyId === policyId || itemReq.itemInsurancePolicyId === policyId) {
        return generateObjectWithStatus(itemReq, status, activePoliciesOfType);
      }
    }

    return itemReq;
  });

  queryClient.setQueryData(itemRequirementsKey, newItemRequirementsData);

  return false;
};

export const optimisticUpdateDocumentStatuses = ({
  documentRequirementsKey,
  previousDocumentRequirements,
  documentsKey,
  previousDocuments,
  docTypeId,
  newStatus
}) => {
  if (!docTypeId) {
    throw new Error(`You must provide a docTypeId.  You provided ${docTypeId}`);
  }

  let docReqNotFound = true;

  const newDocReqs = previousDocumentRequirements.map(dr => {
    if (dr.insuranceDocumentTypeId === docTypeId) {
      docReqNotFound = false;

      if (newStatus === Statuses.Required) {
        const activeDocOfType = previousDocuments.find(d => d.insuranceDocumentTypeId === docTypeId);
        const calcReqStatus = activeDocOfType ? Statuses.Pending : Statuses.NotReceived;

        return { ...dr, status: calcReqStatus };
      }

      if ([Statuses.Required,
        Statuses.Archived,
        Statuses.Active,
        Statuses.Deleted,
        Statuses.Updating].includes(newStatus)) {
        return { ...dr, isLoading: true };
      }

      return { ...dr, status: newStatus };
    }

    return dr;
  });

  if (docReqNotFound) {
    const newDocs = previousDocuments.map(d => {
      if (d.insuranceDocumentTypeId === docTypeId) {
        if ([Statuses.Required,
          Statuses.Archived,
          Statuses.Active,
          Statuses.Deleted,
          Statuses.Updating].includes(newStatus)) {
          return { ...d, isLoading: true };
        }

        return { ...d, status: newStatus };
      }

      return d;
    });

    queryClient.setQueryData(documentsKey, newDocs);
  }

  queryClient.setQueryData(documentRequirementsKey, newDocReqs);

  return docReqNotFound;
};

/**
 * Optimistic update on active records component
 * @param recordId {int}
 * @param activeRecordsKey {string}
 * @param polReqKey {string}
 * @param docReqKey {string}
 * @param itemReqKey {string}
 * @return {void}
 */
export const optimisticUpdateActiveRecords = ({
  recordId,
  activeRecordsKey,
  polReqKey,
  docReqKey,
  itemReqKey
}) => {
  const refreshedPolicyRequirements = queryClient.getQueryData(polReqKey);
  const refreshedDocumentRequirements = queryClient.getQueryData(docReqKey);
  const refreshedItemRequirements = queryClient.getQueryData(itemReqKey);

  const polReqApp = refreshedPolicyRequirements.every(pr => SafeStatuses.includes(pr.status));
  const docReqApp = refreshedDocumentRequirements.every(dr => SafeStatuses.includes(dr.status));
  const itemReqApp = refreshedItemRequirements.every(dr => SafeStatuses.includes(dr.status));

  const status = (polReqApp && docReqApp && itemReqApp) ? Statuses.Approved : Statuses.NotApproved;

  modifyInfiniteListsItem(
    [activeRecordsKey],
    'id',
    recordId,
    record => ({ ...record, recordStatus: status })
  );
};
